import {
  selectDoesUserHaveRbacPermission,
  selectIsJourneyGoalsFeatureFlagOn,
  selectIsJourneyTestingFeatureFlagOn,
  selectUserProjectTimeZone,
} from 'core/userContext';
import { getMetricsTab } from 'modules/journey/components/SettingsTakeover/SettingsTakeover.utils';
import { WorkflowAnalytics } from 'modules/workflows/components/WorkflowAnalytics';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt, useLocation } from 'react-router';
import styled from 'styled-components';
import { useQueryParams } from 'utils/hooks/useQueryParams';
import { useUnsavedChangesPrompt } from 'utils/hooks/useUnsavedChangesPrompt';
import { useWindowSize } from 'utils/hooks/useWindowSize';
import { PROJECT_PERMISSIONS } from 'utils/permissions';

import {
  CLONE_NODE_MODAL,
  DELETE_NODE_MODAL,
  FIX_STUCK_USERS_MODAL,
  JOURNEY_TESTING_MODAL,
  NAVIGATION_TIPS_MODAL,
  NEW_WORKFLOW_ID,
  PUBLISH_JOURNEY_MODAL,
  RESET_TO_PUBLISHED_MODAL,
  SEND_TEST_TRIGGER_MODAL,
  TOGGLE_JOURNEY_MODAL,
  WF_Z_INDEX,
  WORKFLOW_DETAILS_MODAL,
} from '../constants';
import { useJourneyReviewContext } from '../context/JourneyReviewContext';
import { useWorkflowContext } from '../context/WorkflowContext';
import {
  closeTakeover,
  createWorkflowDraftRequest,
  fetchWorkflowRequest,
  getEdges,
  getIsAsyncLoading,
  getIsInProgressTotalPublishedView,
  getIsJourneyMetricsSelectionEnabled,
  getIsJourneyReviewPanelEnabled,
  getIsQuietHoursEnabled,
  getIsYesNoSplitDataToggleEnabled,
  getNodes,
  getOpenedNodeId,
  getWorkflow,
  getWorkflowHasUnsavedChanges,
  getWorkflowType,
  openModal,
  openTakeover,
  saveWorkflowDraftRequest,
  selectOpenModalsList,
  selectOpenTakeovers,
  updateIsInProgressTotalPublishedViewEnabled,
  updateIsMetricsSelectionEnabled,
  updateIsQuietHoursFeatureEnabled,
  updateIsYesNoSplitDataToggleEnabled,
  updateNotesVisibility,
  updateProjectTimeZone,
} from '../ducks';
import { WorkflowType } from '../enums';
import { useNotesVisibility } from '../hooks';
import { useUpdateJourneyUrl } from '../hooks/useUpdateJourneyUrl';
import { EditorSidebar } from './EditorSidebar';
import { ActionBar, FloatingFooterActions } from './Footer';
import { HeaderBar } from './HeaderBar';
import { JourneyReview } from './JourneyReview';
import { LoadingOverlay } from './LoadingOverlay';
import { TileSidebar } from './TileSidebar';
import { WorkflowFlowchart } from './WorkflowFlowchart';
import { WorkflowModal } from './modals';
import { TakeoverType, WorkflowTakeover } from './takeovers';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { isParameterOnlyChange } from 'utils/urlParams';

const UNSAVED_CHANGES_PROMPT = 'Leave page? Changes you made may not be saved.';

const WorkflowContainer = styled.div`
  font-family:
    'Sofia Pro',
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Avenir Next',
    Avenir,
    'Segoe UI',
    'Lucida Grande',
    'Helvetica Neue',
    Helvetica,
    'Fira Sans',
    Roboto,
    Noto,
    'Droid Sans',
    Cantarell,
    Oxygen,
    Ubuntu,
    'Franklin Gothic Medium',
    'Century Gothic',
    'Liberation Sans',
    sans-serif;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  overflow: hidden;
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  position: relative;
`;

const StyledLoadingOverlay = styled(LoadingOverlay)`
  z-index: ${WF_Z_INDEX.OVERLAY};
`;

const WorkflowCmp = (props) => {
  const projectTimeZone = useSelector(selectUserProjectTimeZone);
  const {
    initialWorkflowLoaded: { id: workflowId, workflowType },
  } = useWorkflowContext();

  const isCreativeLibraryAssetsFolderPanelFFEnabled = useFeatureFlag(
    'CreativeLibraryAssetsFolderPanel',
    false,
  );
  const location = useLocation();

  const { openAndRunChecks, runChecks } = useJourneyReviewContext();

  const query = useQueryParams();

  const [headerBarHeight, setHeaderBarHeight] = useState(0);
  const [graph, setGraph] = useState(null);
  const { width, height } = useWindowSize();

  const workflow = useSelector(getWorkflow);

  useUpdateJourneyUrl(workflow.id, workflow.workflowType);

  const openedNodeId = useSelector(getOpenedNodeId);

  const canDraftMessaging = useSelector((state) =>
    selectDoesUserHaveRbacPermission(state)(PROJECT_PERMISSIONS.DraftMessaging),
  );

  const hasUnsavedChanges = useSelector(getWorkflowHasUnsavedChanges);
  useUnsavedChangesPrompt(hasUnsavedChanges, UNSAVED_CHANGES_PROMPT);

  const isJourneyReviewPanelEnabled = useSelector(getIsJourneyReviewPanelEnabled);

  const isYesNoSplitDataToggleEnabled = useSelector(getIsYesNoSplitDataToggleEnabled);
  const isJourneyMetricsSelectionEnabled = useSelector(getIsJourneyMetricsSelectionEnabled);
  const isInProgressTotalPublishedViewEnabled = useSelector(getIsInProgressTotalPublishedView);

  const nodes = useSelector(getNodes);

  const edges = useSelector(getEdges);
  const modals = useSelector(selectOpenModalsList);
  const takeovers = useSelector(selectOpenTakeovers);
  const isAsyncLoading = useSelector(getIsAsyncLoading);
  const { isDraft, isPublished } = useSelector(getWorkflowType);
  const isQuietHoursEnabled = useSelector(getIsQuietHoursEnabled);
  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isJourneyTestingEnabled = useSelector(selectIsJourneyTestingFeatureFlagOn);
  const [isNotesVisible] = useNotesVisibility();

  const { isOpen: isJourneyReviewOpen } = useJourneyReviewContext();

  const isNew = workflowId === NEW_WORKFLOW_ID;
  const { enabled, updatedAt, createdAt } = workflow || {};

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateNotesVisibility(isNotesVisible));
    dispatch(updateIsQuietHoursFeatureEnabled(isQuietHoursEnabled));
    dispatch(updateIsInProgressTotalPublishedViewEnabled(isInProgressTotalPublishedViewEnabled));
  }, []);

  useEffect(() => {
    dispatch(updateIsYesNoSplitDataToggleEnabled(isYesNoSplitDataToggleEnabled));
    dispatch(updateIsMetricsSelectionEnabled(isJourneyMetricsSelectionEnabled));
  }, [isYesNoSplitDataToggleEnabled, isJourneyMetricsSelectionEnabled]);

  useEffect(() => {
    const shouldFetchClonedFromJourney = workflowType === WorkflowType.DRAFT;
    dispatch(fetchWorkflowRequest(Number(workflowId), workflowType, shouldFetchClonedFromJourney));
  }, [workflowId, workflowType]);

  useEffect(() => {
    dispatch(updateProjectTimeZone(projectTimeZone));
  }, [projectTimeZone]);

  /* Callbacks -------------------------------------------------------------- */

  const handleModalOpen = useCallback(
    (type, modalProps) => dispatch(openModal(type, modalProps)),
    [],
  );

  const handleTakeoverOpen = useCallback(
    (type, details) => dispatch(openTakeover(type, details)),
    [],
  );

  const handleCloneNodes = useCallback(
    (modalProps) => handleModalOpen(CLONE_NODE_MODAL, modalProps),
    [],
  );

  const handleDeleteNodes = useCallback(
    (modalProps) => handleModalOpen(DELETE_NODE_MODAL, modalProps),
    [],
  );

  const handleSaveDraft = useCallback(() => {
    if (isJourneyReviewPanelEnabled) {
      dispatch(saveWorkflowDraftRequest({ ...workflow, nodes, edges }, null, runChecks));
    } else {
      dispatch(saveWorkflowDraftRequest({ ...workflow, nodes, edges }));
    }
  }, [workflow, nodes, edges, isJourneyReviewPanelEnabled]);

  const handleReviewJourney = useCallback(() =>
    dispatch(saveWorkflowDraftRequest({ ...workflow, nodes, edges }, null, openAndRunChecks), [
      workflow,
      nodes,
      edges,
    ]),
  );

  const handleCreateDraft = useCallback(() => {
    dispatch(createWorkflowDraftRequest({ ...workflow, goals: [], nodes, edges })); // When creating a draft workflow, we need pass through a blank goals
  }, [workflow, nodes, edges]);

  const handleFixStuckUsersClick = () => handleModalOpen(FIX_STUCK_USERS_MODAL);

  const handleResetToPublishedClick = () => handleModalOpen(RESET_TO_PUBLISHED_MODAL);

  const handleNavigationTips = () => handleModalOpen(NAVIGATION_TIPS_MODAL);

  const handleSendTestTrigger = () =>
    handleModalOpen(isJourneyTestingEnabled ? JOURNEY_TESTING_MODAL : SEND_TEST_TRIGGER_MODAL);

  const handleOpenSettingsTakeover = (overrides = {}) => {
    if (!isJourneyMetricsSelectionEnabled) return;

    handleTakeoverOpen(TakeoverType.Settings, {
      canDraftMessaging,
      onClose: () => dispatch(closeTakeover(TakeoverType.Settings)),
      ...overrides,
    });
  };

  const handleOpenRevisionHistoryTakeover = () => {
    handleTakeoverOpen(TakeoverType.RevisionHistory, {
      canDraftMessaging,
      onClose: () => dispatch(closeTakeover(TakeoverType.RevisionHistory)),
    });
  };

  const handleAboutFlowClick = () =>
    isJourneyMetricsSelectionEnabled
      ? handleOpenSettingsTakeover()
      : handleModalOpen(WORKFLOW_DETAILS_MODAL);

  const handleOnLiveToggleClick = () => handleModalOpen(TOGGLE_JOURNEY_MODAL);

  const handlePublishNewWorkflowClick = () =>
    handleModalOpen(PUBLISH_JOURNEY_MODAL, { hasBeenPublished: false });

  const handlePublishExistingWorkflowClick = () =>
    handleModalOpen(PUBLISH_JOURNEY_MODAL, { hasBeenPublished: true });

  const handleHeaderRefInit = useCallback((el) => {
    if (el !== null) {
      setHeaderBarHeight(el.clientHeight);
    }
  }, []);

  /* Components ------------------------------------------------------------- */

  const SidebarCmp = useMemo(() => {
    const shouldDisplayTilePanel = isDraft && canDraftMessaging;
    if (isJourneyReviewOpen) return <JourneyReview />;
    return shouldDisplayTilePanel && <TileSidebar />;
  }, [canDraftMessaging, isDraft, isJourneyReviewOpen]);
  const selectedNodes = nodes.filter((node) => node.isSelected === true);
  const Toolbar = useMemo(() => {
    return (
      <>
        <FloatingFooterActions />
        <ActionBar
          isNodeEditorOpen={!!openedNodeId}
          isWorkflowEnabled={enabled}
          nodes={nodes}
          onCreateDraft={handleCreateDraft}
          onFixStuckUsersClick={handleFixStuckUsersClick}
          onLiveToggleClick={handleOnLiveToggleClick}
          onOpenCloneNodeModal={handleCloneNodes}
          onOpenDeleteNodeModal={handleDeleteNodes}
          onSaveDraft={handleSaveDraft}
          onSendTestTrigger={handleSendTestTrigger}
          onPublishNewWorkflow={handlePublishNewWorkflowClick}
          onPublishExistingWorkflow={handlePublishExistingWorkflowClick}
          onResetDraft={handleResetToPublishedClick}
          updatedAt={updatedAt}
          workflow={workflow}
        />
      </>
    );
  }, [
    enabled,
    handleCloneNodes,
    handleDeleteNodes,
    handleOnLiveToggleClick,
    handlePublishNewWorkflowClick,
    handlePublishExistingWorkflowClick,
    handleSendTestTrigger,
    nodes,
    openedNodeId,
    updatedAt,
    workflow,
    handleResetToPublishedClick,
    isJourneyReviewOpen,
  ]);

  if (!workflow) return null;

  return (
    <WorkflowContainer data-test="workflow-container">
      <Prompt
        when={hasUnsavedChanges}
        message={(nextLocation) => {
          if (
            isCreativeLibraryAssetsFolderPanelFFEnabled &&
            isParameterOnlyChange(location, nextLocation, ['folderId'])
          ) {
            return true;
          }
          return UNSAVED_CHANGES_PROMPT;
        }}
      />
      <HeaderBar
        isNew={isNew}
        workflowId={workflowId}
        onNavigationTips={handleNavigationTips}
        ref={handleHeaderRefInit}
        nodeEditorOpened={!!openedNodeId}
        createdAt={createdAt}
        workflow={workflow}
        graph={graph}
        onAboutFlowClick={handleAboutFlowClick}
        onResetDraft={handleResetToPublishedClick}
        setIsRevisionHistoryTakeoverOpen={handleOpenRevisionHistoryTakeover}
        handleReviewJourney={handleReviewJourney}
      />
      <MainContainer data-test="main-container">
        <WorkflowFlowchart
          Sidebar={SidebarCmp}
          Toolbar={Toolbar}
          canEditNode={canDraftMessaging}
          edges={edges}
          focusedNode={query.get('nodeid')}
          height={height - headerBarHeight}
          isDraft={isDraft}
          isNew={isNew}
          isReadOnly={isPublished}
          nodes={nodes}
          onLoad={(g) => setGraph(g)}
          onSelectMetricsClick={() =>
            handleOpenSettingsTakeover({ focusedTab: getMetricsTab(isJourneyGoalsEnabled) })
          }
          onTestInit={props.onTestInit}
          openedNodeId={openedNodeId}
          width={width}
          workflowId={workflowId}
        />
        {isPublished && <WorkflowAnalytics workflow={workflow} focusedNodes={selectedNodes} />}
        {openedNodeId && (
          <EditorSidebar
            canDraftMessaging={canDraftMessaging}
            workflowId={workflowId}
            openedNodeId={openedNodeId}
          />
        )}
        {takeovers.map(([takeoverId, takeoverDetails]) => {
          return <WorkflowTakeover key={takeoverId} type={takeoverId} {...takeoverDetails} />;
        })}
      </MainContainer>
      {modals.map(([modalId, modalDetails]) => (
        <WorkflowModal key={modalId} modalType={modalId} {...modalDetails} />
      ))}
      {isAsyncLoading && <StyledLoadingOverlay />}
    </WorkflowContainer>
  );
};

export const Workflow = React.memo(WorkflowCmp);

export default Workflow;
